import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import DashboardView from './views/DashboardView';
import ServicesViewEnhanced from './views/ServicesViewEnhanced';
import PackagesView from './views/PackagesView';
import CategoriesView from './views/CategoriesView';
import OrdersView from './views/OrdersView';
import ServiceProvidersView from './views/ServiceProvidersView';
import ContactsView from './views/ContactsView';
import ReviewsView from './views/ReviewsView';
import UnifiedFeedbackView from './views/UnifiedFeedbackView';
import UsersView from './views/UsersView';
import AnalyticsView from './views/AnalyticsView';
import PromoCodesView from './views/PromoCodesView';
import NotificationsView from './views/NotificationsView';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { adminUser, loading: adminLoading, isAdmin, checkAdminAccess } = useAdminAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [accessChecked, setAccessChecked] = useState(false);

  useEffect(() => {
    const verifyAccess = async () => {
      if (authLoading || adminLoading) {
        return;
      }

      if (!user) {
        // Not logged in - redirect to home
        navigate('/');
        return;
      }

      const hasAccess = await checkAdminAccess();
      
      if (!hasAccess) {
        // Logged in but not an admin - redirect to home
        console.log('Access denied: User is not an admin');
        navigate('/');
        return;
      }

      setAccessChecked(true);
    };

    verifyAccess();
  }, [user, authLoading, adminLoading, navigate, checkAdminAccess]);

  if (authLoading || adminLoading || !accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin || !adminUser) {
    return null; // Will redirect
  }

  const renderView = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardView />;
      case 'services':
        return <ServicesViewEnhanced />;
      case 'packages':
        return <PackagesView />;
      case 'categories':
        return <CategoriesView />;
      case 'orders':
        return <OrdersView />;
      case 'providers':
        return <ServiceProvidersView />;
      case 'contacts':
        return <ContactsView />;
      case 'reviews':
        return <ReviewsView />;
      case 'feedback':
        return <UnifiedFeedbackView />;
      case 'users':
        return <UsersView />;
      case 'notifications':
        return <NotificationsView />;
      case 'analytics':
        return <AnalyticsView />;
      case 'promos':
        return <PromoCodesView />;
      default:
        return <DashboardView />;
    }
  };

  return (
    <AdminLayout activeTab={activeTab} onTabChange={setActiveTab}>
      {renderView()}
    </AdminLayout>
  );
}
